// packages/api/src/hooks/proposals/useUpdateUserPreferences.ts
//
// Phase 09 Plan 09 — PATCH /users/me/preferences (D-04 kanban customization).
// Contrato: docs/contracts/ESCR-pipeline-foundation-07.md.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ApiSuccess, UpdateUserPreferencesPayload, UserPreferences } from '@alamo/types';

import { apiClient } from '../../client';
import { queryKeys } from '../../query-keys';

export type { UserPreferences } from '@alamo/types';

/**
 * PATCH /users/me/preferences — deep-merge no JSONB `users.preferences`.
 *
 * Comportamento backend (D-04 + Plan 07):
 *   - SELECT preferences atual + merge inline + UPDATE
 *   - Arrays substituídos integralmente (não concatenados)
 *   - Chaves top-level NÃO enviadas são PRESERVADAS
 *   - Response = preferences completo pós-merge (rehidrata cache)
 *
 * Permission: nenhuma (JwtAuthGuard global). User só edita próprias prefs
 * — escopo via `user.id` do JWT, não via URL param.
 *
 * Invalidação: `queryKeys.auth.me` (caso `/auth/me` retorne preferences
 * embedded — comum) + `['user-preferences']` para hooks read futuros.
 *
 * Errors esperados:
 *   - 401 AUTH_TOKEN_*
 *   - 404 USER_NOT_FOUND (user vanished mid-session)
 *   - 422 VALIDATION_FAILED (array >20, item não-string, kanban não-objeto)
 *   - 422 INVALID_USER_PREFERENCES (reservado para extensão futura)
 */
export function useUpdateUserPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateUserPreferencesPayload) => {
      const response = await apiClient.patch<ApiSuccess<UserPreferences>>(
        '/users/me/preferences',
        payload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      // `auth.me` reativo — alguns shapes retornam preferences embedded.
      void qc.invalidateQueries({ queryKey: queryKeys.auth.me });
      // Cache local de preferences read-only (caso outro hook leia).
      void qc.invalidateQueries({ queryKey: ['user-preferences'] });
    },
  });
}
